import { useState } from 'react';
import { api, type Subscriber360 } from '../lib/api';

export type LineActionType =
  | 'suspend'
  | 'restore'
  | 'change_plan'
  | 'toggle_roaming'
  | 'set_call_forwarding'
  | 'set_ring_count'
  | 'configure_voicemail'
  | 'sim_swap';

interface Props {
  tenantCode: string;
  agentUid: string;
  contactId: string;
  subscriberId?: string;
  lineId: string;
  phoneNumber?: string;
  action: LineActionType;
  availablePlans?: Subscriber360['availablePlans'];
  onClose: (msg?: string) => void;
}

export function LineActionModal(props: Props) {
  const { action, lineId, phoneNumber, onClose } = props;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => onClose()}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm font-semibold mb-1">{titleFor(action)}</div>
        <div className="text-xs text-gray-500 mb-3" dir="ltr">
          Line: {phoneNumber ?? lineId}
        </div>
        {action === 'suspend' && <SuspendForm {...props} />}
        {action === 'restore' && <RestoreForm {...props} />}
        {action === 'toggle_roaming' && <RoamingForm {...props} />}
        {action === 'change_plan' && <ChangePlanForm {...props} />}
        {action === 'set_call_forwarding' && <ForwardingForm {...props} />}
        {action === 'set_ring_count' && <RingCountForm {...props} />}
        {action === 'configure_voicemail' && <VoicemailForm {...props} />}
        {action === 'sim_swap' && <SimSwapForm {...props} />}
      </div>
    </div>
  );
}

function titleFor(a: LineActionType): string {
  switch (a) {
    case 'suspend': return 'Suspend line';
    case 'restore': return 'Restore line';
    case 'toggle_roaming': return 'Toggle roaming';
    case 'change_plan': return 'Change plan';
    case 'set_call_forwarding': return 'Set call forwarding';
    case 'set_ring_count': return 'Set ring count';
    case 'configure_voicemail': return 'Configure voicemail';
    case 'sim_swap': return 'SIM swap';
  }
}

function ActionButtons({ busy, onCancel, label = 'Apply', danger = false }: { busy: boolean; onCancel: () => void; label?: string; danger?: boolean }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" onClick={onCancel} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">
        Cancel
      </button>
      <button
        type="submit"
        disabled={busy}
        className={`px-3 py-1 text-sm text-white rounded disabled:opacity-50 ${
          danger ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'
        }`}
      >
        {busy ? 'Working…' : label}
      </button>
    </div>
  );
}

function ErrText({ err }: { err: string | null }) {
  return err ? <div className="text-xs text-red-600">{err}</div> : null;
}

function SuspendForm({ tenantCode, agentUid, contactId, lineId, onClose }: Props) {
  const [reason, setReason] = useState<'lost_stolen' | 'customer_request' | 'fraud' | 'administrative' | 'other'>('customer_request');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api.suspendLine(tenantCode, agentUid, contactId, lineId, reason, detail || undefined);
      if (res.ok) onClose('Line suspended.');
      else setErr(res.error ?? 'Failed');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <div>
        <label className="block text-xs text-gray-600 mb-0.5">Reason</label>
        <select value={reason} onChange={(e) => setReason(e.target.value as any)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
          {['lost_stolen', 'customer_request', 'fraud', 'administrative', 'other'].map((r) => (
            <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-0.5">Detail (optional)</label>
        <input value={detail} onChange={(e) => setDetail(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
      </div>
      <ErrText err={err} />
      <ActionButtons busy={busy} onCancel={() => onClose()} label="Suspend" danger />
    </form>
  );
}

function RestoreForm({ tenantCode, agentUid, contactId, lineId, onClose }: Props) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api.restoreLine(tenantCode, agentUid, contactId, lineId);
      if (res.ok) onClose('Line restored.');
      else setErr(res.error ?? 'Failed');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <div className="text-xs text-gray-600">Restore this line to active status?</div>
      <ErrText err={err} />
      <ActionButtons busy={busy} onCancel={() => onClose()} label="Restore" />
    </form>
  );
}

function RoamingForm({ tenantCode, agentUid, contactId, lineId, onClose }: Props) {
  const [enabled, setEnabled] = useState(true);
  const [dataRoaming, setDataRoaming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api.toggleRoaming(tenantCode, agentUid, contactId, lineId, enabled, enabled ? dataRoaming : undefined);
      if (res.ok) onClose(`Roaming ${enabled ? 'enabled' : 'disabled'}.`);
      else setErr(res.error ?? 'Failed');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Roaming enabled
      </label>
      {enabled && (
        <label className="flex items-center gap-2 text-sm pl-5">
          <input type="checkbox" checked={dataRoaming} onChange={(e) => setDataRoaming(e.target.checked)} />
          Allow data while roaming
        </label>
      )}
      <ErrText err={err} />
      <ActionButtons busy={busy} onCancel={() => onClose()} />
    </form>
  );
}

function ChangePlanForm({ tenantCode, agentUid, contactId, subscriberId, availablePlans, onClose }: Props) {
  const plans = availablePlans ?? [];
  const [planCode, setPlanCode] = useState(plans[0]?.planCode ?? plans[0]?.code ?? '');
  const [preview, setPreview] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  if (!subscriberId) {
    return (
      <div className="text-xs text-red-700">
        Subscriber ID is missing — change plan is not available.
        <ActionButtons busy={false} onCancel={() => onClose()} label="Close" />
      </div>
    );
  }
  if (!plans.length) {
    return (
      <div className="text-xs text-gray-700">
        No plans available.
        <div className="mt-2">
          <ActionButtons busy={false} onCancel={() => onClose()} label="Close" />
        </div>
      </div>
    );
  }

  async function doPreview() {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.changePlan(tenantCode, agentUid, contactId, subscriberId!, planCode, true);
      if (res.ok) setPreview(res.data);
      else setErr(res.error ?? 'Preview failed');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  async function confirm() {
    setBusy(true);
    setErr(null);
    try {
      const res = await api.changePlan(tenantCode, agentUid, contactId, subscriberId!, planCode, false);
      if (res.ok) onClose(`Plan changed to ${planCode}.`);
      else setErr(res.error ?? 'Failed');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="space-y-2.5">
      <div>
        <label className="block text-xs text-gray-600 mb-0.5">New plan</label>
        <select value={planCode} onChange={(e) => { setPlanCode(e.target.value); setPreview(null); }} className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
          {plans.map((p) => {
            const code = p.planCode ?? p.code ?? '';
            const price = p.monthlyPrice ?? p.price;
            return (
              <option key={code} value={code}>
                {p.name ?? code}{price !== undefined ? ` — ${price} ${p.currency ?? 'ILS'}/mo` : ''}
              </option>
            );
          })}
        </select>
      </div>
      {preview ? (
        <pre className="text-[11px] bg-gray-50 border border-gray-200 rounded p-2 overflow-auto max-h-32 whitespace-pre-wrap">
          {JSON.stringify(preview, null, 2)}
        </pre>
      ) : (
        <button type="button" onClick={doPreview} disabled={busy} className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50">
          Preview proration
        </button>
      )}
      <ErrText err={err} />
      <div className="flex justify-end gap-2 pt-1">
        <button type="button" onClick={() => onClose()} className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50">
          Cancel
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={busy || !preview}
          className="px-3 py-1 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
          title={!preview ? 'Preview first' : ''}
        >
          {busy ? 'Working…' : 'Confirm change'}
        </button>
      </div>
    </div>
  );
}

function ForwardingForm({ tenantCode, agentUid, contactId, lineId, onClose }: Props) {
  const [forwardType, setForwardType] = useState<'unconditional' | 'busy' | 'no_answer' | 'unreachable'>('unconditional');
  const [destination, setDestination] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api.setCallForwarding(tenantCode, agentUid, contactId, lineId, forwardType, destination.trim(), enabled);
      if (res.ok) onClose('Call forwarding updated.');
      else setErr(res.error ?? 'Failed');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <div>
        <label className="block text-xs text-gray-600 mb-0.5">Type</label>
        <select value={forwardType} onChange={(e) => setForwardType(e.target.value as any)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
          {['unconditional', 'busy', 'no_answer', 'unreachable'].map((t) => (
            <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-0.5">Destination (E.164)</label>
        <input value={destination} onChange={(e) => setDestination(e.target.value)} placeholder="+972525550199" required className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono" />
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Rule enabled
      </label>
      <ErrText err={err} />
      <ActionButtons busy={busy} onCancel={() => onClose()} />
    </form>
  );
}

function RingCountForm({ tenantCode, agentUid, contactId, lineId, onClose }: Props) {
  const [ringCount, setRingCount] = useState(5);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api.setRingCount(tenantCode, agentUid, contactId, lineId, ringCount);
      if (res.ok) onClose(`Ring count set to ${ringCount}.`);
      else setErr(res.error ?? 'Failed');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <div>
        <label className="block text-xs text-gray-600 mb-0.5">Rings before voicemail (2-10)</label>
        <input
          type="number"
          min={2}
          max={10}
          value={ringCount}
          onChange={(e) => setRingCount(Number(e.target.value))}
          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
        />
        <div className="text-[11px] text-gray-500 mt-0.5">~{ringCount * 5}s before voicemail</div>
      </div>
      <ErrText err={err} />
      <ActionButtons busy={busy} onCancel={() => onClose()} />
    </form>
  );
}

function VoicemailForm({ tenantCode, agentUid, contactId, lineId, onClose }: Props) {
  const [enabled, setEnabled] = useState(true);
  const [rings, setRings] = useState(5);
  const [greeting, setGreeting] = useState<'default' | 'custom' | 'name_only'>('default');
  const [transcription, setTranscription] = useState(true);
  const [notifyBySms, setNotifyBySms] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const res = await api.configureVoicemail(tenantCode, agentUid, contactId, lineId, {
        enabled,
        ringsBeforeVoicemail: rings,
        greeting,
        transcription,
        notifyBySms,
      });
      if (res.ok) onClose('Voicemail configured.');
      else setErr(res.error ?? 'Failed');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Voicemail enabled
      </label>
      {enabled && (
        <>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Rings before voicemail</label>
            <input
              type="number"
              min={2}
              max={10}
              value={rings}
              onChange={(e) => setRings(Number(e.target.value))}
              className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Greeting</label>
            <select value={greeting} onChange={(e) => setGreeting(e.target.value as any)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
              {['default', 'custom', 'name_only'].map((g) => (
                <option key={g} value={g}>{g.replace(/_/g, ' ')}</option>
              ))}
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={transcription} onChange={(e) => setTranscription(e.target.checked)} />
            Transcription (voicemail to text)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={notifyBySms} onChange={(e) => setNotifyBySms(e.target.checked)} />
            SMS notification for new voicemails
          </label>
        </>
      )}
      <ErrText err={err} />
      <ActionButtons busy={busy} onCancel={() => onClose()} />
    </form>
  );
}

function SimSwapForm({ tenantCode, agentUid, contactId, lineId, onClose }: Props) {
  const [newIccid, setNewIccid] = useState('');
  const [reason, setReason] = useState<'damaged' | 'lost' | 'upgrade' | 'device_change' | 'other'>('damaged');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (newIccid.trim().length < 10) {
      setErr('ICCID must be at least 10 characters');
      return;
    }
    if (!confirm('SIM swap is a sensitive action. Continue?')) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await api.simSwap(tenantCode, agentUid, contactId, lineId, newIccid.trim(), reason);
      if (res.ok) onClose('SIM swap initiated.');
      else setErr(res.error ?? 'Failed');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <form onSubmit={submit} className="space-y-2.5">
      <div>
        <label className="block text-xs text-gray-600 mb-0.5">New ICCID</label>
        <input value={newIccid} onChange={(e) => setNewIccid(e.target.value)} required maxLength={30} className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono" />
      </div>
      <div>
        <label className="block text-xs text-gray-600 mb-0.5">Reason</label>
        <select value={reason} onChange={(e) => setReason(e.target.value as any)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm">
          {['damaged', 'lost', 'upgrade', 'device_change', 'other'].map((r) => (
            <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>
      <ErrText err={err} />
      <ActionButtons busy={busy} onCancel={() => onClose()} label="Swap SIM" danger />
    </form>
  );
}
